import React, { useEffect, useRef } from 'react';
import './Modal.css';

/**
 * Generic Modal component with mobile and desktop support
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal should close
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size: 'small', 'medium', 'large', 'fullscreen'
 * @param {boolean} props.closeOnOverlayClick - Whether to close when clicking overlay (default: true)
 * @param {boolean} props.closeOnEscape - Whether to close when pressing Escape (default: true)
 * @param {boolean} props.showCloseButton - Whether to show the X close button (default: true)
 * @param {string} props.className - Additional CSS class for modal
 * @param {Object} props.style - Additional inline styles
 * @param {React.ReactNode} props.footer - Optional footer content
 * @param {boolean} props.preventBodyScroll - Whether to prevent body scroll when modal is open (default: true)
 * @param {string} props.ariaLabelledBy - ARIA labelledby attribute
 * @param {string} props.ariaDescribedBy - ARIA describedby attribute
 */
export default function Modal({
    isOpen = false,
    onClose,
    title,
    children,
    size = 'medium',
    closeOnOverlayClick = true,
    closeOnEscape = true,
    showCloseButton = true,
    className = '',
    style = {},
    footer = null,
    preventBodyScroll = true,
    ariaLabelledBy,
    ariaDescribedBy
}) {
    const modalRef = useRef(null);
    const overlayRef = useRef(null);
    const closeButtonRef = useRef(null);
    const previousFocusRef = useRef(null);

    // Handle escape key press
    useEffect(() => {
        if (!isOpen || !closeOnEscape) return;

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose && onClose();
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [isOpen, closeOnEscape, onClose]);

    // Handle body scroll prevention
    useEffect(() => {
        if (!preventBodyScroll) return;

        if (isOpen) {
            // Store original overflow value
            const originalOverflow = document.body.style.overflow;
            const originalPaddingRight = document.body.style.paddingRight;

            // Calculate scrollbar width
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

            // Prevent body scroll and compensate for scrollbar
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollbarWidth}px`;

            return () => {
                document.body.style.overflow = originalOverflow;
                document.body.style.paddingRight = originalPaddingRight;
            };
        }
    }, [isOpen, preventBodyScroll]);

    // Focus management
    useEffect(() => {
        if (isOpen) {
            previousFocusRef.current = document.activeElement;
            // Focus the close button or first focusable element
            const focusableElements = modalRef.current?.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );

            if (focusableElements && focusableElements.length > 0) {
                focusableElements[0].focus();
            }
            return () => {
                if (previousFocusRef.current?.isConnected) {
                    previousFocusRef.current.focus();
                }
            };
        }
    }, [isOpen]);

    // Handle overlay click
    const handleOverlayClick = (event) => {
        if (closeOnOverlayClick && event.target === overlayRef.current) {
            onClose && onClose();
        }
    };

    // Handle close button click
    const handleCloseClick = () => {
        onClose && onClose();
    };

    const handleKeyDown = (event) => {
        if (event.key !== 'Tab') return;
        const focusableElements = Array.from(modalRef.current?.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), '
            + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []);
        if (focusableElements.length === 0) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    if (!isOpen) return null;

    const modalClasses = [
        'modal',
        `modal--${size}`,
        className
    ].filter(Boolean).join(' ');

    return (
        <div
            className="modal-overlay"
            ref={overlayRef}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
        >
            <div
                className={modalClasses}
                ref={modalRef}
                style={style}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
            >
                {(title || showCloseButton) && (
                    <div className="modal__header">
                        {title && (
                            <h2 className="modal__title" id={ariaLabelledBy || 'modal-title'}>
                                {title}
                            </h2>
                        )}
                        {showCloseButton && (
                            <button
                                ref={closeButtonRef}
                                className="modal__close-button"
                                onClick={handleCloseClick}
                                aria-label="Close modal"
                                type="button"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}

                <div className="modal__content" id={ariaDescribedBy || 'modal-content'}>
                    {children}
                </div>

                {footer && (
                    <div className="modal__footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
